import {
  Accessor,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  on,
  onCleanup,
  untrack,
} from 'solid-js';
import {
  isFeatureExtractionPaneEligible,
  type FeatureExtractionContextType,
} from '@mobile-mapping/providers';
import type { DrawingTool } from '@trimble/geospatial';
import { asError, type ILogger } from '@trimble/logging';
import type { Web3DViewer } from '@technology/web3d';
import type { LineEditingPlugin } from '@technology/web3d-plugin-line-editing';
import type { FeaturesServicePlugin } from '@trimble/web3d-plugin-features-service';

import {
  createFeatureExtractionController,
  type FeatureExtractionController,
} from './feature-extraction-controller';
import type { PointCloudRoot } from './feature-extraction-types';

export interface CadEditingViewerWrapper {
  viewer: Web3DViewer;
  lineEditingPlugin: LineEditingPlugin;
  featuresServicePlugin: FeaturesServicePlugin;
}

export interface UseCadEditingPaneParams {
  featureExtraction: FeatureExtractionContextType;
  featureExtractionLogger: ILogger;
  isEditingEligible: Accessor<boolean>;
  viewModeType: Accessor<string | undefined>;
  /** Loaded-cloud CSIB only — not sticky currentLoadedCsib (no cross-cloud borrow). */
  sceneCsib: Accessor<string | undefined>;
  pointCloudRoots: Accessor<PointCloudRoot[]>;
  getViewerWrapper(): CadEditingViewerWrapper | undefined;
  fetchGeoDataFiles(csib: string): Promise<Map<string, ArrayBuffer>>;
  onCadEditingEnabled?(): void;
  onCadEditingDisabled?(): void;
  onRequestDrawingTool?(tool: DrawingTool): void;
}

export interface UseCadEditingPaneResult {
  cadEditingMode: Accessor<boolean>;
  cadEditingSession: Accessor<FeatureExtractionController | undefined>;
  isCadEditingEligible: Accessor<boolean>;
}

interface GeoDataCacheEntry {
  csib: string;
  geoDataFiles: Map<string, ArrayBuffer>;
}

interface GeoFetchFailure {
  csib: string;
  nonce: number;
}

export function useCadEditingPane(
  params: UseCadEditingPaneParams
): UseCadEditingPaneResult {
  const cadEditingMode = params.featureExtraction.cadEditingMode;
  const [cadEditingSession, setCadEditingSession] = createSignal<
    FeatureExtractionController | undefined
  >(undefined);
  const [geoFetchNonce, setGeoFetchNonce] = createSignal(0);
  const [geoFetchFailure, setGeoFetchFailure] = createSignal<
    GeoFetchFailure | undefined
  >(undefined);

  const isCadEditingEligible = createMemo(
    () =>
      isFeatureExtractionPaneEligible(
        params.isEditingEligible(),
        params.viewModeType()
      ) && params.featureExtraction.featureRepository() !== undefined
  );

  const geoDataFetchKey = createMemo(() => {
    const csib = params.sceneCsib();
    if (!csib || !isCadEditingEligible()) {
      return undefined;
    }
    return { csib, nonce: geoFetchNonce() };
  });

  const disposeCadEditingSession = (): void => {
    const session = untrack(() => cadEditingSession());
    if (session) {
      session.dispose();
      setCadEditingSession(undefined);
      untrack(() => params.onCadEditingDisabled?.());
    }
  };

  const [geoDataEntry] = createResource(
    geoDataFetchKey,
    async ({ csib, nonce }): Promise<GeoDataCacheEntry | undefined> => {
      try {
        const geoDataFiles = await params.fetchGeoDataFiles(csib);
        if (
          untrack(
            () => geoFetchNonce() === nonce && params.sceneCsib() === csib
          )
        ) {
          setGeoFetchFailure(undefined);
        }
        return { csib, geoDataFiles };
      } catch (error: unknown) {
        params.featureExtractionLogger.warn(
          'Failed to fetch geodata files for CAD editing pane session',
          { csib },
          asError(error)
        );
        const isLatestFetch = untrack(
          () => geoFetchNonce() === nonce && params.sceneCsib() === csib
        );
        if (isLatestFetch) {
          setGeoFetchFailure({ csib, nonce });
        }
        return undefined;
      }
    }
  );

  createEffect(() => {
    const failure = geoFetchFailure();
    if (!failure) {
      return;
    }
    if (
      !cadEditingMode() ||
      geoFetchNonce() !== failure.nonce ||
      params.sceneCsib() !== failure.csib
    ) {
      setGeoFetchFailure(undefined);
      return;
    }
    setGeoFetchFailure(undefined);
    disposeCadEditingSession();
  });

  // Eligibility does not change on a layer reload, so a finished layer load
  // retries a failed geodata fetch.
  createEffect(
    on(
      [cadEditingMode, () => params.featureExtraction.layersLoadStatus()],
      ([enabled, status], previous) => {
        if (!enabled || geoDataEntry.loading) {
          return;
        }
        const onlyStatusChanged = previous?.[0] === enabled;
        if (onlyStatusChanged && status !== 'success') {
          return;
        }
        const csib = params.sceneCsib();
        const entry = geoDataEntry();
        if (csib && entry?.csib !== csib) {
          setGeoFetchNonce((nonce) => nonce + 1);
        }
      }
    )
  );

  // Bind only these inputs. PaneViewer enable()/activate() reads and writes
  // CadEditingCapability isActive; tracking that rebuilds the session forever.
  createEffect(
    on(
      [
        cadEditingMode,
        isCadEditingEligible,
        params.sceneCsib,
        geoDataEntry,
        // Region change swaps featureRepository; rebuild the session.
        () => params.featureExtraction.featureRepository(),
        () => params.getViewerWrapper(),
      ],
      ([enabled, eligible, csib, cache, , viewerWrapper]) => {
        const existing = untrack(() => cadEditingSession());

        if (!enabled || !eligible) {
          if (existing) {
            disposeCadEditingSession();
          }
          return;
        }

        if (!viewerWrapper || !csib) {
          if (!existing) {
            params.featureExtractionLogger.info(
              'CAD editing enabled but pane session not started',
              {
                hasViewer: Boolean(viewerWrapper),
                hasCsib: Boolean(csib),
              }
            );
          }
          return;
        }

        if (!cache || cache.csib !== csib) {
          disposeCadEditingSession();
          return;
        }

        const session = createFeatureExtractionController({
          viewer: viewerWrapper.viewer,
          lineEditingPlugin: viewerWrapper.lineEditingPlugin,
          context: params.featureExtraction,
          logger: params.featureExtractionLogger,
          csib,
          geoDataFiles: cache.geoDataFiles,
          pointCloudRoots: params.pointCloudRoots,
        });
        setCadEditingSession(session);
        untrack(() => params.onCadEditingEnabled?.());

        onCleanup(() => {
          session.dispose();
          if (untrack(() => cadEditingSession()) === session) {
            setCadEditingSession(undefined);
            untrack(() => params.onCadEditingDisabled?.());
          }
        });
      }
    )
  );

  createEffect(
    on(
      [cadEditingSession, params.featureExtraction.desiredDrawingTool],
      ([session, desiredTool]) => {
        if (!session) {
          return;
        }
        const activeTool = untrack(() => session.activeTool());
        if (desiredTool === activeTool) {
          return;
        }
        if (desiredTool === undefined) {
          session.cancelActiveTool();
          return;
        }
        if (params.onRequestDrawingTool) {
          params.onRequestDrawingTool(desiredTool);
          return;
        }
        session.toggleTool(desiredTool);
      }
    )
  );

  onCleanup(() => {
    disposeCadEditingSession();
  });

  return {
    cadEditingMode,
    cadEditingSession,
    isCadEditingEligible,
  };
}
